import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Download,
  Flag,
  FlagOff,
  ArrowRight,
  ArrowLeft,
  Layers,
  BadgeCheck,
  Image as ImageIcon,
} from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { getCase, flagCase, listBatchCases, formatCaseId } from "@/lib/api";
import ConfidenceRing from "@/components/ConfidenceRing";
import ScoreBadge, { ScoreDot } from "@/components/ScoreBadge";
import StatusBadge from "@/components/StatusBadge";
import { SkeletonBlock } from "@/components/Skeletons";
import { downloadReportPdf } from "@/lib/exports";
import { scoreLabel, scoreTier } from "@/lib/mockData";

const SCORE_TIER_TEXT = {
  genuine: "text-emerald-400",
  inconclusive: "text-amber-400",
  ai: "text-red-400",
};

export default function Report() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // localThumbnail is an object URL forwarded from NewAnalysis via router state.
  // Used as a fallback when the case has no public thumbnail URL (file uploads).
  const localThumbnail = location.state?.localThumbnail || null;

  const [caseData, setCaseData] = useState(null);
  const [batchPeers, setBatchPeers] = useState([]);
  const [err, setErr] = useState(null);
  const [flagging, setFlagging] = useState(false);

  useEffect(() => {
    let active = true;
    setErr(null);
    setCaseData(null);
    getCase(caseId)
      .then((c) => {
        if (!active) return;
        setCaseData(c);
        if (c.batchId) {
          listBatchCases(c.batchId).then((bs) => active && setBatchPeers(bs));
        } else {
          setBatchPeers([]);
        }
      })
      .catch((e) => active && setErr(e.message || "Failed to load report"));
    return () => {
      active = false;
    };
  }, [caseId]);

  const tier = caseData ? scoreTier(caseData.score) : "inconclusive";
  const ringGlow =
    tier === "genuine"
      ? "ring-glow-genuine"
      : tier === "inconclusive"
      ? "ring-glow-inconclusive"
      : "ring-glow-ai";

  const metadataCompleteness = useMemo(() => {
    if (!caseData?.signals?.length) return null;
    const hasCompletenessNumber = typeof caseData.metadata_completeness === "number";
    if (!hasCompletenessNumber) return { unavailable: true };
    const found = caseData.signals.filter((s) => s.data_found).length;
    const total = caseData.signals.length;
    const pct = Math.round(caseData.metadata_completeness * 100);
    return { found, total, pct };
  }, [caseData]);

  const { prev, next } = useMemo(() => {
    if (!caseData || batchPeers.length === 0) return { prev: null, next: null };
    const idx = batchPeers.findIndex((c) => c.id === caseData.id);
    return {
      prev: idx > 0 ? batchPeers[idx - 1] : null,
      next: idx >= 0 && idx < batchPeers.length - 1 ? batchPeers[idx + 1] : null,
    };
  }, [caseData, batchPeers]);

  // Resolved thumbnail: server value first, local object URL as fallback.
  const thumbnail = caseData?.thumbnail || localThumbnail || null;
  const displayId = caseData ? formatCaseId(caseData.id) : formatCaseId(caseId);

  const handleExport = () => {
    if (!caseData) return;
    downloadReportPdf(caseData);
    toast.success("Export complete", { description: `yoobikwitus-${displayId}.pdf` });
  };

  const handleFlag = async () => {
    if (!caseData) return;
    setFlagging(true);
    try {
      const updated = await flagCase(caseData.id, !caseData.flagged);
      setCaseData({ ...caseData, flagged: updated.flagged, status: updated.status });
      toast.success(updated.flagged ? "Flagged for review" : "Flag removed");
    } finally {
      setFlagging(false);
    }
  };

  if (err) {
    return (
      <div className="max-w-3xl mx-auto" data-testid="report-error">
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-sm px-4 py-3">
          {err}
        </div>
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="max-w-6xl mx-auto space-y-6" data-testid="report-loading">
        <div className="grid lg:grid-cols-[1fr_360px] gap-6">
          <div className="rounded-xl border border-[#272A30] bg-[#111318] p-8">
            <SkeletonBlock className="h-44 w-44 rounded-full mx-auto" />
            <SkeletonBlock className="mt-6 h-5 w-1/3 mx-auto" />
          </div>
          <div className="rounded-xl border border-[#272A30] bg-[#111318] p-6">
            <SkeletonBlock className="h-4 w-1/2" />
            <SkeletonBlock className="mt-3 h-3 w-3/4" />
            <SkeletonBlock className="mt-3 h-3 w-2/3" />
          </div>
        </div>
        <div className="rounded-xl border border-[#272A30] bg-[#111318] p-6 space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonBlock key={i} className="h-8 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="report-page">
      {/* Top hero */}
      <header className="grid lg:grid-cols-[1fr_360px] gap-6">
        <section
          className="relative rounded-xl border bg-[#111318] p-8 flex flex-col items-center justify-center border-[#272A30]"
          data-testid="confidence-card"
        >
          <div className={`rounded-full ${ringGlow}`}>
            <ConfidenceRing score={caseData.score} size={196} stroke={14} showLabel={false} />
          </div>
          <div className="mt-5 text-[11px] uppercase tracking-[0.22em] text-zinc-500">
            Overall verdict
          </div>
          <div
            className={`mt-1 text-xl font-semibold tracking-tight ${SCORE_TIER_TEXT[tier]}`}
            data-testid="overall-verdict"
          >
            {caseData.verdict?.headline || scoreLabel(caseData.score)}
          </div>
          {caseData.verdict?.detail && (
            <div className="mt-2 text-xs text-zinc-400 max-w-md text-center leading-relaxed">
              {caseData.verdict.detail}
            </div>
          )}
          {caseData.verdict?.primary_signal && (
            <div className="mt-2 text-[10px] uppercase tracking-[0.18em] text-zinc-600">
              {caseData.verdict.primary_signal === "hive"
                ? "Primarily based on AI detection"
                : "Primarily based on metadata"}
            </div>
          )}
          <div className="mt-2 text-xs text-zinc-500 max-w-md text-center">
            Score range — <span className="text-emerald-400">&gt;70 genuine</span> ·{" "}
            <span className="text-amber-400">40–70 inconclusive</span> ·{" "}
            <span className="text-red-400">&lt;40 AI-generated</span>
          </div>
        </section>

        <aside className="rounded-xl border border-[#272A30] bg-[#111318] p-6 space-y-5">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 mb-2">Asset</div>
            <div className="flex gap-3 items-start">
              {thumbnail ? (
                <img
                  src={thumbnail}
                  alt=""
                  className="h-16 w-24 rounded-md object-cover ring-1 ring-[#272A30] flex-shrink-0"
                />
              ) : (
                <div className="h-16 w-24 rounded-md bg-[#1A1D24] ring-1 ring-[#272A30] flex items-center justify-center flex-shrink-0">
                  <ImageIcon className="h-5 w-5 text-zinc-600" />
                </div>
              )}
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">
                  {caseData.title || caseData.fileName || displayId}
                </div>
                <div className="mt-1 text-[11px] text-zinc-500 mono truncate">
                  {caseData.fileName || ""}
                </div>
                <div className="mt-1">
                  <StatusBadge status={caseData.flagged ? "flagged" : caseData.status} />
                </div>
              </div>
            </div>
          </div>

          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">Case ID</dt>
              <dd className="mono text-zinc-200" data-testid="case-id">
                {displayId}
              </dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">Submitted</dt>
              <dd className="text-zinc-200">
                {new Date(caseData.submittedAt).toLocaleString()}
              </dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">Source</dt>
              <dd className="mono text-zinc-200 truncate">
                {caseData.sourceDomain || (caseData.sourceUrl?.startsWith("upload://") ? "Uploaded file" : "—")}
              </dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">Verdict</dt>
              <dd>
                <ScoreBadge score={caseData.score} withLabel={false} />
              </dd>
            </div>
          </dl>

          <div className="space-y-2">
            <div className="flex gap-2">
              <button
                onClick={handleExport}
                data-testid="export-pdf-button"
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-white text-[#0A0A0B] font-semibold text-sm px-4 py-2.5 hover:bg-zinc-200 transition-colors"
              >
                <Download className="h-4 w-4" /> Export PDF
              </button>
              <button
                onClick={handleFlag}
                disabled={flagging}
                data-testid="flag-report-button"
                className={`inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm transition-colors ${
                  caseData.flagged
                    ? "border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-500/20"
                    : "border-[#272A30] text-zinc-300 hover:bg-[#1A1D24]"
                }`}
              >
                {caseData.flagged ? (
                  <>
                    <FlagOff className="h-4 w-4" /> Unflag
                  </>
                ) : (
                  <>
                    <Flag className="h-4 w-4" /> Flag
                  </>
                )}
              </button>
            </div>
            <button
              type="button"
              onClick={() =>
                toast.info("Coming soon", {
                  description: "Provenance certificates are still in development.",
                })
              }
              data-testid="mint-provenance-certificate-button"
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-sky-500/30 bg-sky-500/5 px-4 py-2.5 text-sm font-medium text-sky-300 hover:bg-sky-500/10 hover:border-sky-500/50 transition-colors"
            >
              <BadgeCheck className="h-4 w-4" />
              Mint provenance certificate (coming soon)
            </button>
          </div>
        </aside>
      </header>

      {/* Hive AI detection */}
      {caseData.hiveResult && !caseData.hiveResult.stub && (
        <section
          className="rounded-xl border border-[#272A30] bg-[#111318] p-6"
          data-testid="hive-detection-card"
        >
          <h2 className="text-base font-semibold tracking-tight mb-1">AI detection</h2>
          <p className="text-xs text-zinc-500 mb-4">This result is factored into the overall verdict above.</p>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="rounded-lg bg-[#1A1D24] border border-[#272A30] p-4">
              <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1">Result</div>
              <div className={`text-lg font-semibold ${caseData.hiveResult.ai_generated ? "text-red-400" : "text-emerald-400"}`}>
                {caseData.hiveResult.ai_generated ? "AI generated" : "Not AI generated"}
              </div>
            </div>
            <div className="rounded-lg bg-[#1A1D24] border border-[#272A30] p-4">
              <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1">Confidence</div>
              <div className="text-lg font-semibold text-zinc-200">
                {Math.round(caseData.hiveResult.ai_score * 100)}%
              </div>
              <div className="text-xs text-zinc-500 mt-0.5">AI generated probability</div>
            </div>
            <div className="rounded-lg bg-[#1A1D24] border border-[#272A30] p-4">
              <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1">Likely source</div>
              <div className="text-lg font-semibold text-zinc-200 capitalize">
                {caseData.hiveResult.likely_generator
                  ? `${caseData.hiveResult.likely_generator} (${Math.round(caseData.hiveResult.generator_score * 100)}%)`
                  : "Unknown"}
              </div>
            </div>
          </div>
          {caseData.hiveResult.deepfake_score > 0.5 && (
            <div className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-sm px-4 py-3">
              Deepfake indicators detected ({Math.round(caseData.hiveResult.deepfake_score * 100)}% confidence).
            </div>
          )}
        </section>
      )}

      {/* Signals */}
      <section
        className="rounded-xl border border-[#272A30] bg-[#111318] overflow-hidden"
        data-testid="signals-table"
      >
        <div className="px-5 py-4 border-b border-[#272A30] flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold tracking-tight">Metadata signal breakdown</h2>
            <p className="text-xs text-zinc-500 mt-0.5">
              Each signal contributes to the overall confidence score.
            </p>
          </div>
          {metadataCompleteness && (
            <div className="text-right">
              {metadataCompleteness.unavailable ? (
                <p className="text-xs text-zinc-500">Completeness data not available for this report</p>
              ) : (
                <>
                  <p className="text-sm font-medium text-zinc-200">
                    {metadataCompleteness.found} of {metadataCompleteness.total} signals found data
                  </p>
                  <p className="text-xs text-zinc-500">{metadataCompleteness.pct}% of metadata found</p>
                </>
              )}
            </div>
          )}
        </div>
        {caseData.signals && caseData.signals.length > 0 ? (
          <table className="w-full">
            <thead>
              <tr className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">
                <th className="px-5 py-3 text-left font-medium w-1/4">Signal</th>
                <th className="px-5 py-3 text-left font-medium">Finding</th>
                <th className="px-5 py-3 text-left font-medium w-28">Score</th>
              </tr>
            </thead>
            <tbody>
              {caseData.signals.map((s) => (
                <tr
                  key={s.name}
                  data-testid={`signal-row-${s.name.replace(/\s+/g, "-").toLowerCase()}`}
                  className="border-t border-[#272A30]/60"
                >
                  <td className="px-5 py-3.5 align-top">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <ScoreDot score={s.score} />
                      {s.name}
                    </div>
                  </td>
                  <td className="px-5 py-3.5 align-top text-sm text-zinc-300 leading-relaxed">
                    {s.finding}
                  </td>
                  <td className="px-5 py-3.5 align-top">
                    <ScoreBadge score={s.score} withLabel={false} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-8 text-center text-sm text-zinc-500">
            No signal data available for this analysis.
          </div>
        )}
      </section>

      {/* Batch navigation */}
      {caseData.batchId && (
        <section
          className="rounded-xl border border-[#272A30] bg-[#111318] p-4 flex items-center justify-between"
          data-testid="batch-nav"
        >
          <div className="flex items-center gap-3 text-sm text-zinc-400">
            <Layers className="h-4 w-4" />
            Part of <span className="mono text-zinc-200">{caseData.batchId}</span>
            <button
              onClick={() => navigate(`/batch/${caseData.batchId}`)}
              className="text-zinc-300 hover:text-white underline-offset-4 hover:underline"
              data-testid="open-batch-link"
            >
              Open batch view
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => prev && navigate(`/report/${prev.id}`)}
              disabled={!prev}
              data-testid="prev-in-batch"
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#272A30] px-3 py-1.5 text-xs text-zinc-300 hover:bg-[#1A1D24] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Previous
            </button>
            <button
              onClick={() => next && navigate(`/report/${next.id}`)}
              disabled={!next}
              data-testid="next-in-batch"
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#272A30] px-3 py-1.5 text-xs text-zinc-300 hover:bg-[#1A1D24] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
